import React, {RefObject} from 'react';
import {observer} from 'mobx-react';
import I18n from 'app/i18n';
import {configStore, cupController, cupStore} from 'app/store/global';
import type {CupUserTokenPayload} from 'app/store/CupStore';
import {startSteamAuthFlow} from 'app/utils/steamLink';
import AsideItem from 'site_version/com/AsideItem';

interface CupMessage {
    type?: 'cup:request-user-token' | 'cup:resize' | 'cup:link-steam'
    height?: number
}

const ISO_DATE_LENGTH = 10;
const MIN_WIDGET_HEIGHT_PX = 120;

@observer
class CupAside extends React.Component {
    iframeRef: RefObject<HTMLIFrameElement> = React.createRef();

    componentDidMount(): void {
        window.addEventListener('message', this.handleMessage);
    }

    componentWillUnmount(): void {
        window.removeEventListener('message', this.handleMessage);
    }

    buildSrc = (): string => {
        const params = new URLSearchParams({
            _v: new Date().toISOString().slice(0, ISO_DATE_LENGTH),
            balance: cupStore.balance,
            centrifugoUrl: cupStore.centrifugoPath,
            countryCode: cupStore.countryCode,
            currency: cupStore.currency,
            email: cupStore.email,
            embeddedInWidget: 'true',
            externalId: cupStore.currentExternalId,
            lang: cupStore.lang,
            nickname: cupStore.nickname,
            parentOrigin: cupStore.parentOrigin,
            steamId64: cupStore.steamId64,
            timezone: cupStore.timezone,
            userToken: cupStore.userToken
        });

        return `${configStore.CUP_WIDGET_URL}?${params.toString()}`;
    };

    postUserToken = ({userToken, nickname, countryCode, email, balance, currency, steamId64}: CupUserTokenPayload): void => {
        this.iframeRef.current?.contentWindow?.postMessage(
            {balance, countryCode, currency, email, nickname, steamId64, type: 'cup:user-token', userToken},
            cupStore.lobbyOrigin
        );
    };

    handleMessage = (event: MessageEvent<CupMessage>): void => {
        if (event.source !== this.iframeRef.current?.contentWindow) {
            return;
        }

        const {data} = event;

        switch (data?.type) {
        case 'cup:request-user-token':
            cupController.refreshUserToken()
                .then(this.postUserToken)
                .catch(() => null);
            break;
        case 'cup:link-steam':
            startSteamAuthFlow();
            break;
        case 'cup:resize':
            if (typeof data.height === 'number' && data.height > 0) {
                const iframe = this.iframeRef.current;

                if (iframe) {
                    iframe.style.height = `${Math.max(data.height, MIN_WIDGET_HEIGHT_PX)}px`;
                }
            }
            break;
        default:
            break;
        }
    };

    render(): React.ReactNode {
        if (!configStore.isReady) {
            return null;
        }

        return (
            <AsideItem
                header={
                    <div className="aside-item-heading__title">
                        <h3>{I18n.t('cup.title')}</h3>
                    </div>
                }
                itemClassName="cup-aside"
                name="cup"
            >
                <iframe
                    allow="clipboard-write"
                    id="cup-widget"
                    ref={this.iframeRef}
                    scrolling="no"
                    src={this.buildSrc()}
                    style={{border: 'none', display: 'block', width: '100%'}}
                    title="cup-widget"
                />
            </AsideItem>
        );
    }
}

export default CupAside;
